import type { KeyboardEvent } from "react";
import {
  POPOVER_LIST_OPTION_ATTR,
  POPOVER_LIST_ROOT_ATTR,
  POPOVER_LIST_SEARCH_ATTR,
} from "./popover-list-search-attrs";
import { ensurePopoverListSearchKeyRedirect } from "./popover-list-search-keys";

export {
  POPOVER_LIST_OPTION_ATTR,
  POPOVER_LIST_ROOT_ATTR,
  POPOVER_LIST_SEARCH_ATTR,
} from "./popover-list-search-attrs";

const SEARCH_SELECTOR = `[${POPOVER_LIST_SEARCH_ATTR}]:not([disabled])`;
const OPTION_SELECTOR = `[${POPOVER_LIST_OPTION_ATTR}]:not([disabled]):not([data-disabled])`;

/**
 * Finds the list search field inside a Radix overlay root.
 */
export function findPopoverListSearchInput(
  root: EventTarget | null,
): HTMLElement | null {
  if (!(root instanceof HTMLElement)) return null;
  const el = root.querySelector<HTMLElement>(SEARCH_SELECTOR);
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return el;
  }
  return null;
}

function overlayRootFromEvent(event: Event): HTMLElement | null {
  if (event.currentTarget instanceof HTMLElement) return event.currentTarget;
  if (event.target instanceof HTMLElement) return event.target;
  return null;
}

/** Opening pointerup/click can steal focus after a single rAF. */
const OPENING_POINTER_FOCUS_MS = 400;

function pointerIsInsideOverlayBody(
  search: HTMLElement,
  target: EventTarget | null,
): boolean {
  if (!(target instanceof Node)) return false;
  if (target === search || search.contains(target)) return false;
  const root = search.closest(`[${POPOVER_LIST_ROOT_ATTR}]`);
  return root?.contains(target) === true;
}

/**
 * Keep caret in a list search field through the opening pointerup/click
 * and any focusin that lands outside the field (trigger, menu root, item).
 * Use from custom overlays that are not Radix Popover/Dropdown/ContextMenu.
 * Returns a cleanup that must run when the overlay unmounts.
 */
export function persistPopoverListSearchFocus(search: HTMLElement): () => void {
  let stopped = false;
  const rafIds: number[] = [];
  let timeout0 = 0;
  let timeoutOpen = 0;
  // The search field's own window: a pop-out composer window has its own.
  const ownerDoc: Document = search.ownerDocument;
  const ownerWin: Window = ownerDoc.defaultView ?? window;

  const focusSearch = (): void => {
    if (stopped) return;
    if (!search.isConnected) return;
    if (ownerDoc.activeElement === search) return;
    search.focus({ preventScroll: true });
  };

  const onOpeningPointer = (event: Event): void => {
    if (stopped) return;
    if (!search.isConnected) return;
    if (pointerIsInsideOverlayBody(search, event.target)) return;
    focusSearch();
    rafIds.push(ownerWin.requestAnimationFrame(focusSearch));
  };

  const onOpeningFocusIn = (event: Event): void => {
    if (stopped) return;
    if (!search.isConnected) return;
    const target = event.target;
    if (target === search) return;
    if (target instanceof Node && search.contains(target)) return;
    focusSearch();
    rafIds.push(ownerWin.requestAnimationFrame(focusSearch));
  };

  const capture = true;
  const cleanup = (): void => {
    if (stopped) return;
    stopped = true;
    for (const id of rafIds) ownerWin.cancelAnimationFrame(id);
    ownerWin.clearTimeout(timeout0);
    ownerWin.clearTimeout(timeoutOpen);
    ownerWin.removeEventListener("pointerup", onOpeningPointer, { capture });
    ownerWin.removeEventListener("click", onOpeningPointer, { capture });
    ownerDoc.removeEventListener("focusin", onOpeningFocusIn, { capture });
  };

  ensurePopoverListSearchKeyRedirect(ownerWin);
  focusSearch();
  queueMicrotask(focusSearch);
  rafIds.push(
    ownerWin.requestAnimationFrame(() => {
      focusSearch();
      rafIds.push(ownerWin.requestAnimationFrame(focusSearch));
    }),
  );
  timeout0 = ownerWin.setTimeout(focusSearch, 0);
  ownerWin.addEventListener("pointerup", onOpeningPointer, { capture });
  ownerWin.addEventListener("click", onOpeningPointer, { capture });
  ownerDoc.addEventListener("focusin", onOpeningFocusIn, { capture });
  timeoutOpen = ownerWin.setTimeout(cleanup, OPENING_POINTER_FOCUS_MS);

  return cleanup;
}

/**
 * Radix `onOpenAutoFocus` handler: put the caret in the list search field.
 * Call after the consumer handler. No-ops when the overlay has no search —
 * Radix then focuses the first option so Enter still works.
 *
 * A single rAF is too early: the opening pointerup still focuses the trigger
 * (`modal={false}` popovers). Persist through pointerup/click so the caret
 * is real, not a leftover autofocus blink.
 */
export function focusPopoverListSearchOnOpen(event: Event): void {
  const search = findPopoverListSearchInput(overlayRootFromEvent(event));
  if (search == null) return;
  event.preventDefault();
  persistPopoverListSearchFocus(search);
}

function isListSearchField(el: EventTarget | null): boolean {
  return el instanceof HTMLElement && el.hasAttribute(POPOVER_LIST_SEARCH_ATTR);
}

/**
 * Stops Radix menu typeahead from moving focus to a matching item. Call from
 * the search input's `onKeyDown` (bubble), never from a parent `onKeyDownCapture`.
 * Capture-stop on a parent blocks the input so the caret looks focused but
 * typing lands on the menu. Escape still bubbles so the overlay can close.
 */
export function stopPopoverListSearchTypeahead(
  event: KeyboardEvent<Element>,
): void {
  if (event.nativeEvent.isComposing) return;
  if (event.ctrlKey || event.altKey || event.metaKey) return;
  if (event.key.length !== 1) return;
  if (
    !isListSearchField(event.target) &&
    !isListSearchField(event.currentTarget)
  ) {
    return;
  }
  event.stopPropagation();
}

/**
 * Enter confirms the first enabled option, then character keys stay in the
 * search field. Call from the search input after local key handlers.
 */
export function handlePopoverListSearchKeyDown(
  event: KeyboardEvent<Element>,
): void {
  confirmPopoverListSearchEnter(event);
  stopPopoverListSearchTypeahead(event);
}

/**
 * Enter in a list search field confirms the first enabled option. Keyboard
 * lists that already handled Enter (highlight + arrows) no-op via
 * `defaultPrevented`. Call from the search input or the overlay root.
 */
export function confirmPopoverListSearchEnter(
  event: KeyboardEvent<Element>,
): boolean {
  if (event.defaultPrevented) return false;
  if (event.key !== "Enter") return false;
  if (event.nativeEvent.isComposing) return false;
  if (
    !isListSearchField(event.target) &&
    !isListSearchField(event.currentTarget)
  ) {
    return false;
  }
  const from =
    event.currentTarget instanceof HTMLElement
      ? event.currentTarget
      : event.target;
  if (!(from instanceof HTMLElement)) return false;
  const root = from.closest(`[${POPOVER_LIST_ROOT_ATTR}]`);
  if (!(root instanceof HTMLElement)) return false;
  const option = root.querySelector<HTMLElement>(OPTION_SELECTOR);
  if (option == null) return false;
  event.preventDefault();
  event.stopPropagation();
  option.click();
  return true;
}
